// S6 (v0.17.0) — ONVIF Discovery page.
//
// Two-panel layout: scan controls on the left, device results on the
// right. The scan API returns a job ID immediately and we poll the
// detail endpoint every 2 seconds until status flips from "running"
// to "complete" or "failed". Direct probe is a fire-once form that
// renders its result inline (no DB row).

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  useAddDiscoveredDevice,
  useDiscovery,
  useLatestDiscovery,
  useProbeDevice,
  useStartScan,
  type DiscoveredDevice,
  type ProbeResult,
} from "@/api/s6";
import { useDeviceProfiles } from "@/api/s5";
import { useSites } from "@/api/sites";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";

export default function DiscoveryPage() {
  const [networkRange, setNetworkRange] = useState("");
  const [timeoutSec, setTimeoutSec] = useState(5);
  const [scanId, setScanId] = useState<string | null>(null);
  const [probeOpen, setProbeOpen] = useState(false);
  const [probeIp, setProbeIp] = useState("");
  const [probePort, setProbePort] = useState(80);
  const [probeUser, setProbeUser] = useState("");
  const [probePass, setProbePass] = useState("");
  const [probeResult, setProbeResult] = useState<ProbeResult | null>(null);

  const start = useStartScan();
  const probe = useProbeDevice();
  const latest = useLatestDiscovery();
  // Poll while the scan is running.
  const activeId = scanId ?? latest.data?.id ?? undefined;
  const activeStatus = scanId ? "polling" : latest.data?.status;
  const discovery = useDiscovery(activeId, {
    refetchInterval:
      activeStatus === "running" || activeStatus === "polling" ? 2000 : false,
  });

  useEffect(() => {
    if (scanId && discovery.data?.status === "complete") {
      // Pin the result to the panel; future scans replace it.
    }
  }, [scanId, discovery.data?.status]);

  const devices: DiscoveredDevice[] = discovery.data?.devices ?? [];

  async function handleScan() {
    setScanId(null);
    const r = await start.mutateAsync({
      network_range: networkRange.trim() || undefined,
      timeout: timeoutSec,
    });
    setScanId(r.discovery_id);
  }

  async function handleProbe() {
    setProbeResult(null);
    const r = await probe.mutateAsync({
      ip: probeIp.trim(),
      port: probePort || 80,
      username: probeUser || undefined,
      password: probePass || undefined,
    });
    setProbeResult(r);
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {/* ============= Left: Scan controls ============= */}
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold">ONVIF Discovery</h1>
          <p className="text-sm text-muted-foreground">
            Scans your local network for ONVIF-compatible cameras via
            WS-Discovery (UDP multicast on 239.255.255.250).
          </p>
        </div>

        <Card>
          <CardContent className="space-y-3 p-4">
            <div>
              <Label>Network range (optional)</Label>
              <Input
                placeholder="e.g. 192.168.1.0/24 (leave blank for auto)"
                value={networkRange}
                onChange={(e) => setNetworkRange(e.target.value)}
              />
            </div>
            <div>
              <Label>Timeout: {timeoutSec}s</Label>
              <input
                type="range"
                min={5}
                max={30}
                step={1}
                value={timeoutSec}
                onChange={(e) => setTimeoutSec(Number(e.target.value))}
                className="w-full"
              />
            </div>
            <Button
              onClick={handleScan}
              disabled={start.isPending || discovery.data?.status === "running"}
              className="w-full"
            >
              {discovery.data?.status === "running"
                ? "Scanning network…"
                : "Scan Network"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-2 p-4">
            <button
              type="button"
              className="text-sm font-semibold underline"
              onClick={() => setProbeOpen((v) => !v)}
            >
              {probeOpen ? "▼" : "▶"} Probe a specific IP
            </button>
            {probeOpen && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>IP address</Label>
                    <Input
                      value={probeIp}
                      onChange={(e) => setProbeIp(e.target.value)}
                      placeholder="192.168.1.100"
                    />
                  </div>
                  <div>
                    <Label>Port</Label>
                    <Input
                      type="number"
                      value={probePort}
                      onChange={(e) =>
                        setProbePort(Number(e.target.value) || 80)
                      }
                    />
                  </div>
                  <div>
                    <Label>Username (optional)</Label>
                    <Input
                      value={probeUser}
                      onChange={(e) => setProbeUser(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Password (optional)</Label>
                    <PasswordInput
                      value={probePass}
                      onChange={(e) => setProbePass(e.target.value)}
                    />
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleProbe}
                  disabled={!probeIp || probe.isPending}
                  className="w-full"
                >
                  {probe.isPending ? "Probing…" : "Probe"}
                </Button>
                {probeResult && (
                  <div
                    className={
                      "rounded border px-3 py-2 text-sm " +
                      (probeResult.error
                        ? "border-red-500/40 bg-red-500/10 text-red-600"
                        : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700")
                    }
                  >
                    {probeResult.error ? (
                      <span>Failed: {probeResult.error}</span>
                    ) : (
                      <span>
                        Found <strong>{probeResult.manufacturer ?? "Unknown"}</strong>{" "}
                        {probeResult.model ?? ""}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ============= Right: Results ============= */}
      <div className="space-y-3">
        {discovery.data ? (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {devices.length} device{devices.length === 1 ? "" : "s"} found
              </h2>
              <span className="rounded bg-muted px-2 py-1 text-xs uppercase">
                {discovery.data.status}
              </span>
            </div>
            {discovery.data.status === "failed" && (
              <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
                {discovery.data.error_message ?? "Scan failed"}
              </p>
            )}
            {devices.length === 0 &&
              discovery.data.status === "complete" && (
                <p className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700">
                  No ONVIF devices responded. Try a different network range
                  or use direct probe.
                </p>
              )}
            <div className="space-y-2">
              {devices.map((d) => (
                <DeviceCard
                  key={d.id}
                  device={d}
                  discoveryId={discovery.data!.id}
                />
              ))}
            </div>
          </>
        ) : (
          <Card className="border-dashed">
            <CardContent className="space-y-2 p-6 text-center">
              <p className="text-sm text-muted-foreground">
                Scan your network to discover ONVIF cameras.
              </p>
              <Button onClick={handleScan} disabled={start.isPending}>
                Start First Scan
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function DeviceCard({
  device,
  discoveryId,
}: {
  device: DiscoveredDevice;
  discoveryId: string;
}) {
  const [open, setOpen] = useState(false);
  const profileLabels = useMemo(
    () =>
      Array.from(
        new Set((device.onvif_profiles ?? []).map((p) => p.type || "S")),
      ),
    [device.onvif_profiles],
  );
  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-sm font-semibold">
              {device.ip_address}:{device.port}
            </div>
            <div className="text-sm text-muted-foreground">
              {device.manufacturer ?? "Unknown"}{" "}
              {device.model && `· ${device.model}`}
            </div>
          </div>
          {device.is_added ? (
            <span className="rounded bg-emerald-500/10 px-2 py-1 text-xs text-emerald-700">
              Added
              {device.camera_id && (
                <Link
                  to={`/cameras/${device.camera_id}`}
                  className="ml-1 underline"
                >
                  view
                </Link>
              )}
            </span>
          ) : (
            <Button size="sm" onClick={() => setOpen(true)}>
              Add as Camera
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {profileLabels.map((p) => (
            <span
              key={p}
              className="rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-bold text-blue-700"
            >
              Profile {p}
            </span>
          ))}
        </div>
        {device.stream_uri && (
          <div className="truncate font-mono text-xs text-muted-foreground">
            {device.stream_uri}
          </div>
        )}
        {open && (
          <AddFromDiscoveryForm
            device={device}
            discoveryId={discoveryId}
            onClose={() => setOpen(false)}
          />
        )}
      </CardContent>
    </Card>
  );
}

function AddFromDiscoveryForm({
  device,
  discoveryId,
  onClose,
}: {
  device: DiscoveredDevice;
  discoveryId: string;
  onClose: () => void;
}) {
  const sites = useSites();
  const profiles = useDeviceProfiles();
  const add = useAddDiscoveredDevice();
  const [name, setName] = useState(
    `${device.manufacturer ?? "ONVIF"} ${device.model ?? device.ip_address}`.trim(),
  );
  const [siteId, setSiteId] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [profileId, setProfileId] = useState("");
  const [tagDraft, setTagDraft] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  function addTag() {
    const t = tagDraft.trim();
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]);
    setTagDraft("");
  }

  async function handleAdd() {
    setError(null);
    if (!siteId) {
      setError("Pick a site");
      return;
    }
    try {
      await add.mutateAsync({
        discoveryId,
        deviceId: device.id,
        payload: {
          name,
          site_id: siteId,
          username: username || undefined,
          password: password || undefined,
          profile_id: profileId || undefined,
          tags: tags.length ? tags : undefined,
        },
      });
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        .response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Add failed");
    }
  }

  return (
    <div className="space-y-2 rounded border bg-muted/30 p-3">
      <div>
        <Label>Camera name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <Label>Site</Label>
        <select
          className="w-full rounded border bg-background px-2 py-1 text-sm"
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
        >
          <option value="">Choose a site…</option>
          {(sites.data?.items ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.code ? ` (${s.code})` : ""}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Username</Label>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <div>
          <Label>Password</Label>
          <PasswordInput
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
      </div>
      <div>
        <Label>Device Profile (optional)</Label>
        <select
          className="w-full rounded border bg-background px-2 py-1 text-sm"
          value={profileId}
          onChange={(e) => setProfileId(e.target.value)}
        >
          <option value="">No profile</option>
          {(profiles.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Label>Tags</Label>
        <div className="flex gap-2">
          <Input
            value={tagDraft}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              }
            }}
          />
          <Button type="button" variant="outline" onClick={addTag}>
            Add
          </Button>
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {tags.map((t) => (
            <button
              key={t}
              type="button"
              className="rounded-full bg-muted px-2 py-0.5 text-xs"
              onClick={() => setTags(tags.filter((x) => x !== t))}
            >
              {t} ×
            </button>
          ))}
        </div>
      </div>
      {error && (
        <p className="rounded border border-red-500/40 bg-red-500/10 px-2 py-1 text-xs text-red-600">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleAdd} disabled={add.isPending}>
          {add.isPending ? "Adding…" : "Add Camera"}
        </Button>
      </div>
    </div>
  );
}
